"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { navigateWithQueryParams } from "@/utils/navigationUtils";
import { autoIcon, bundleIcon } from "@/lib/gcsImagePaths";
import { homeIcon, specialtyIcon, underline } from "@/lib/gcsImagePaths";
import GcsImage from "../common/GcsImage";
import HeroCarousel from "./HeroCarousel";
import {
  autoIconDim,
  bundleIconDim,
  homeIconDim,
  specialtyIconDim,
  underlineDim,
} from "@/lib/comparisonData";

interface HeroSectionProps {
  userCity: string;
}

export default function HeroSection({ userCity }: HeroSectionProps) {
  const router = useRouter();
  const [selectedInsuranceType, setSelectedInsuranceType] = useState<
    string | null
  >(null);

  const handleInsuranceSelection = (insuranceType: string) => {
    setSelectedInsuranceType(insuranceType);
    if (insuranceType === "specialty") {
      navigateWithQueryParams(router, "/compare");
      return;
    }
    navigateWithQueryParams(router, "/compare", {
      step: "insurance-type",
      insuranceType: insuranceType,
    });
  };

  return (
    <section className="relative overflow-hidden">
      <div className="flex flex-col xl:grid xl:grid-cols-[1fr_600px] min-h-[600px]">
        {/* Left Side - Content with Gradient Background */}
        <div className="bg-[#E2F3FB] text-[#090739] flex items-center px-4 sm:px-8 md:px-16 xl:pl-[max(4rem,calc(50vw-700px+1rem))] py-12 md:py-16">
          <div className="max-w-3xl xl:max-w-4xl w-full">
            <p className="text-sm md:text-sm lg:text-base font-bold text-[#209FBD] mb-3 tracking-wide uppercase h-[1.5rem] md:h-[1.25rem] flex items-center">
              Top Insurance Agents{" "}
              {userCity || <span className="inline-block w-0">&nbsp;</span>}
            </p>
            <h2 className="text-3xl sm:text-3xl md:text-3xl lg:text-4xl 2xl:text-5xl font-bold mb-4 md:mb-6 leading-tight">
              Welcome to your insurance agency{" "}
              <span className="relative inline-block pb-2">
                concierge.
                <GcsImage
                  path={underline}
                  width={underlineDim.width}
                  height={underlineDim.height}
                  alt="Decorative underline"
                  className="absolute top-full left-0 w-full h-auto -mt-3 scale-y-[0.54] pointer-events-none"
                  loading="eager"
                />
              </span>
            </h2>
            <p className="text-lg sm:text-lg md:text-xl lg:text-xl 2xl:text-2xl mb-6 md:mb-8 opacity-70">
              Insurance shopping is a hassle. We reshop your policy 
              automatically so you never shop for insurance again.
            </p>

            {/* Insurance Type Buttons */}
            <div className="grid grid-cols-2 md:flex gap-4 mb-6 max-w-lg">
              <Button
                variant="outline"
                onClick={() => handleInsuranceSelection("home_auto")}
                className={`${
                  selectedInsuranceType === "home_auto"
                    ? "bg-white border-[#090739] border-[3px]"
                    : "bg-white border-transparent border-[3px]"
                } text-[#090739] hover:bg-white h-auto py-4 px-4 flex flex-col items-center gap-2 flex-1 md:min-w-[140px] transition-colors`}
              >
                <div className="h-12 flex items-center justify-center">
                  <GcsImage
                    path={bundleIcon}
                    width={bundleIconDim.width}
                    height={bundleIconDim.height}
                    alt="Home + Auto Insurance"
                    className="w-[60px] h-12 object-contain"
                  />
                </div>
                <span className="text-base font-medium">Home + Auto</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => handleInsuranceSelection("home")}
                className={`${
                  selectedInsuranceType === "home"
                    ? "bg-white border-[#090739] border-[3px]"
                    : "bg-white border-transparent border-[3px]"
                } text-[#090739] hover:bg-white h-auto py-4 px-4 flex flex-col items-center gap-2 flex-1 md:min-w-[140px] transition-colors`}
              >
                <div className="h-12 flex items-center justify-center">
                  <GcsImage
                    path={homeIcon}
                    width={homeIconDim.width}
                    height={homeIconDim.height}
                    alt="Home Insurance"
                    className="w-[60px] h-12 object-contain"
                  />
                </div>
                <span className="text-base font-medium">Home</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => handleInsuranceSelection("auto")}
                className={`${
                  selectedInsuranceType === "auto"
                    ? "bg-white border-[#090739] border-[3px]"
                    : "bg-white border-transparent border-[3px]"
                } text-[#090739] hover:bg-white h-auto py-4 px-4 flex flex-col items-center gap-2 flex-1 md:min-w-[140px] transition-colors`}
              >
                <div className="h-12 flex items-center justify-center">
                  <GcsImage
                    path={autoIcon}
                    width={autoIconDim.width}
                    height={autoIconDim.height}
                    alt="Auto Insurance"
                    className="w-12 h-12 object-contain"
                  />
                </div>
                <span className="text-base font-medium">Auto</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => handleInsuranceSelection("specialty")}
                className={`${
                  selectedInsuranceType === "specialty"
                    ? "bg-white border-[#090739] border-[3px]"
                    : "bg-white border-transparent border-[3px]"
                } text-[#090739] hover:bg-white h-auto py-4 px-4 flex flex-col items-center gap-2 flex-1 md:min-w-[140px] transition-colors`}
              >
                <div className="h-12 flex items-center justify-center">
                  <GcsImage
                    path={specialtyIcon}
                    width={specialtyIconDim.width}
                    height={specialtyIconDim.height}
                    alt="Specialty Insurance"
                    className="w-9 h-9 object-contain"
                  />
                </div>
                <span className="text-base font-medium">Specialty</span>
              </Button>
            </div>

            <Button
              variant={"compare"}
              onClick={() => navigateWithQueryParams(router, "/compare")}
            >
              COMPARE QUOTES
            </Button>
          </div>
        </div>

        {/* Right Side - Carousel */}
        <div className="relative w-full xl:w-[600px] xl:min-h-[600px]">
          <HeroCarousel />
        </div>
      </div>
    </section>
  );
}
